import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Move compare element into card gallery if it exists (with delay handling)
    this.#initCompareElementMovement();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Initializes the compare element movement with multiple strategies for delayed loading.
   */
  #initCompareElementMovement() {
    const productId = this.dataset.productId;
    const cardGallery = this.refs.cardGallery;
    
    if (!productId || !cardGallery) return;

    // Strategy 1: Try immediate search
    this.#attemptCompareElementMove(productId, cardGallery);

    // Strategy 2: Set up periodic checks for delayed elements
    this.#setupPeriodicCompareCheck(productId, cardGallery);

    // Strategy 3: Set up mutation observer for DOM changes
    this.#setupCompareElementObserver(productId, cardGallery);

    // Strategy 4: Listen for common compare app events
    this.#setupCompareAppEventListeners(productId, cardGallery);
  }

  /**
   * Attempts to find and move the compare element.
   * @param {string} productId - The product ID
   * @param {HTMLElement} cardGallery - The card gallery container
   * @returns {boolean} True if element was found and moved
   */
  #attemptCompareElementMove(productId, cardGallery) {
    // Check if already moved
    if (cardGallery.querySelector('.sp-compare-moved-to-gallery')) {
      return true;
    }

    // Search in multiple locations
    const searchSelectors = [
      `.sp-compare-main.sp-compare-checkbox[data-product-id="${productId}"]`,
      `.sp-compare-main[data-product-id="${productId}"]`,
      `.sp-compare-checkbox[data-product-id="${productId}"]`,
    ];

    let compareElement = null;

    // Search globally first
    for (const selector of searchSelectors) {
      compareElement = document.querySelector(selector);
      if (compareElement) break;
    }

    // Search locally if not found globally
    if (!compareElement) {
      for (const selector of ['.sp-compare-main', '.sp-compare-checkbox']) {
        compareElement = this.querySelector(selector) || 
                        this.parentElement?.querySelector(selector);
        if (compareElement) break;
      }
    }

    if (compareElement instanceof HTMLElement) {
      this.#moveCompareElementToGallery(compareElement, cardGallery);
      return true;
    }

    return false;
  }

  /**
   * Sets up periodic checks for compare elements that load with delay.
   * @param {string} productId - The product ID
   * @param {HTMLElement} cardGallery - The card gallery container
   */
  #setupPeriodicCompareCheck(productId, cardGallery) {
    let attempts = 0;
    const maxAttempts = 20; // Check for up to 20 seconds
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (this.#attemptCompareElementMove(productId, cardGallery) || attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 1000); // Check every second
  }

  /**
   * Sets up mutation observer for dynamically added compare elements.
   * @param {string} productId - The product ID
   * @param {HTMLElement} cardGallery - The card gallery container
   */
  #setupCompareElementObserver(productId, cardGallery) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
            // Check if this node or its children contain compare elements
            const isCompareElement = node.classList.contains('sp-compare-main') || 
                                   node.classList.contains('sp-compare-checkbox');
            
            const hasCompareElement = node.querySelector('.sp-compare-main, .sp-compare-checkbox');
            
            if (isCompareElement || hasCompareElement) {
              if (this.#attemptCompareElementMove(productId, cardGallery)) {
                observer.disconnect();
                return;
              }
            }
          }
        }
      }
    });

    // Observe document body for globally added elements
    observer.observe(document.body, { childList: true, subtree: true });

    // Clean up after 30 seconds
    setTimeout(() => observer.disconnect(), 30000);
  }

  /**
   * Sets up event listeners for common compare app events.
   * @param {string} productId - The product ID
   * @param {HTMLElement} cardGallery - The card gallery container
   */
  #setupCompareAppEventListeners(productId, cardGallery) {
    // Listen for common events that compare apps might trigger
    const events = ['DOMContentLoaded', 'load', 'compare:loaded', 'sp-compare:ready'];
    
    events.forEach(eventName => {
      document.addEventListener(eventName, () => {
        setTimeout(() => {
          this.#attemptCompareElementMove(productId, cardGallery);
        }, 100);
      }, { once: true });
    });

    // Also check when scripts load
    const checkForScripts = () => {
      const scripts = document.querySelectorAll('script[src*="compare"], script[src*="sp-compare"]');
      if (scripts.length > 0) {
        setTimeout(() => {
          this.#attemptCompareElementMove(productId, cardGallery);
        }, 500);
      }
    };

    checkForScripts();
    setTimeout(checkForScripts, 2000);
  }

  /**
   * Moves the compare element to the card gallery and sets up event handlers.
   * @param {HTMLElement} compareElement - The compare element to move
   * @param {HTMLElement} cardGallery - The card gallery container
   */
  #moveCompareElementToGallery(compareElement, cardGallery) {
    // Move the element into the card gallery
    cardGallery.appendChild(compareElement);
    
    // Add a class to indicate it's been moved for styling purposes
    compareElement.classList.add('sp-compare-moved-to-gallery');
    
    // Ensure it's visible if it was hidden
    if (compareElement.style.display === 'none') {
      compareElement.style.display = '';
    }
    
    // Add click event listener to prevent navigation when clicking compare
    compareElement.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
    });

    // Dispatch custom event to notify that element was moved
    this.dispatchEvent(new CustomEvent('compare-element-moved', {
      detail: { productId: this.dataset.productId, compareElement }
    }));
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      history.replaceState({}, '', url.toString());
    }

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * Public method to manually trigger compare element movement.
   * This can be called externally to retry finding and moving compare elements.
   */
  retryCompareElementMovement() {
    this.#initCompareElementMovement();
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

// Global helper function to manually trigger compare element movement
// @ts-ignore
window.moveCompareElements = function() {
  const productCards = document.querySelectorAll('product-card');
  productCards.forEach(card => {
    if (card instanceof ProductCard) {
      card.retryCompareElementMovement();
    }
  });
};

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
